import React, { useContext } from 'react';
import { useAppSelector } from '@/features';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { type Connector, type ConnectorStatusEnum, type Vehicle } from '@/types/swagger';
import { Divider, Tooltip } from '@mui/material';
import CircularProgress from 'components/CircularProgress';
import { CHARGING_PERMISSION } from 'constants/ChargingPermission';
import { getClassByStatus } from 'util/getComponentIcon';

import ConnectorInfo from './ConnectorInfo';
import ConnectorTitle from './ConnectorTitle';
import FinalSoc from './FinalSoc';
import ManualFields from './ManualFields';
import { ConnectorDetailsContainer, ConnectorInfoContainer } from './styled';

interface ConnectorAvailableProps {
  status: ConnectorStatusEnum;
  connector: Connector | null;
  isLoading: boolean;
  selectedVehicle: Vehicle | null;
  setSelectedVehicle: (vehicle: Vehicle) => void;
  odometerValue: string;
  setOdometerValue: (value: string) => void;
  socLimit: number;
  setSocLimit: (value: number) => void;
  prevSocLimit: number;
  handleUpdateCharge: () => void;
  handleToggleCharge: () => Promise<void>;
}

const ConnectorAvailable: React.FC<ConnectorAvailableProps> = ({
  status,
  connector,
  isLoading,
  selectedVehicle,
  setSelectedVehicle,
  odometerValue,
  setOdometerValue,
  socLimit,
  setSocLimit,
  prevSocLimit,
  handleUpdateCharge,
  handleToggleCharge,
}) => {
  const { user } = useContext(UserSessionContext);
  const chargePoint = useAppSelector((state) =>
    connector?.charge_point ? state.chargePoints.byId[connector.charge_point] : null
  );

  return (
    <ConnectorDetailsContainer className="connector-details-container flex-fill">
      <ConnectorTitle connectorName={connector?.verbose_name || '- - -'} />
      <ConnectorInfoContainer className={getClassByStatus(status)}>
        <ConnectorInfo connector={connector} status={status} />
      </ConnectorInfoContainer>
      <div className="d-flex flex-column flex-fill">
        {!isLoading ? (
          <div className="d-flex flex-column flex-fill justify-content-end">
            {chargePoint?.fill_manual_fields && (
              <ManualFields
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={setSelectedVehicle}
                odometerValue={odometerValue}
                setOdometerValue={setOdometerValue}
              />
            )}
            <Divider className="my-3" />
            <div className="connector-details-actions">
              <FinalSoc
                disabled={!user?.groups?.includes(CHARGING_PERMISSION)}
                socLimit={socLimit}
                setSocLimit={setSocLimit}
              />
              <Tooltip
                disableInteractive
                disableHoverListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
                disableTouchListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
                title="Usted no tiene permisos suficientes para gestionar cargas. Contacte a un administrador."
              >
                <div>
                  <Tooltip
                    disableInteractive
                    disableHoverListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                    }
                    disableTouchListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                    }
                    title="Cambie el valor de SoC máximo para volver a actualizarlo."
                    placement="top"
                  >
                    <div className="mb-3">
                      <Button
                        className="$tw-w-full"
                        variant="secondary"
                        disabled={
                          !user?.groups?.includes(CHARGING_PERMISSION) || socLimit === prevSocLimit
                        }
                        onClick={handleUpdateCharge}
                      >
                        Actualizar SoC máximo
                      </Button>
                    </div>
                  </Tooltip>
                  <Tooltip
                    disableInteractive
                    disableHoverListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) ||
                      !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                    }
                    disableTouchListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) ||
                      !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                    }
                    title="Debe llenar la identificación del vehículo antes de poder iniciar una carga"
                  >
                    <Button
                      className="$tw-w-full"
                      disabled={
                        !user?.groups?.includes(CHARGING_PERMISSION) ||
                        (chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                      }
                      onClick={handleToggleCharge}
                    >
                      Iniciar carga
                    </Button>
                  </Tooltip>
                </div>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column flex-fill justify-content-end">
            <CircularProgress />
          </div>
        )}
      </div>
    </ConnectorDetailsContainer>
  );
};

export default ConnectorAvailable;
